// ============================================================
// AttendancePage.jsx — FINAL PRODUCTION VERSION with Time Tracking
// ============================================================

import React, { useState, useEffect } from "react";
import FaceScanner from "./FaceScanner";
import Navigation from "./Navigation";
import { ref, get, set, update } from "firebase/database";
import { database } from "./firebase";

// 🔥 MODES
const MODES = [
  { key: "checkin",  label: "CHECK IN",  icon: "🟢", color: "#22c55e" },
  { key: "checkout", label: "CHECK OUT", icon: "🔴", color: "#ef4444" },
  { key: "halfday",  label: "HALF DAY",  icon: "🌙", color: "#f59e0b" },
];

// ✅ FIXED DATE FUNCTION (NO BUG)
const getTodayDate = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().split("T")[0];
};

// Calculate total worked hours
const calculateTotalHours = (checkInTime, checkOutTime, isHalfDay = false) => {
  if (isHalfDay) return 4.0;
  if (!checkInTime || !checkOutTime) return 0;
  
  const [inHours, inMinutes] = checkInTime.split(":").map(Number);
  const [outHours, outMinutes] = checkOutTime.split(":").map(Number);
  
  let totalMinutes = (outHours * 60 + outMinutes) - (inHours * 60 + inMinutes);
  if (totalMinutes < 0) totalMinutes += 24 * 60;
  
  const hours = totalMinutes / 60;
  return Math.round(hours * 100) / 100;
};

export default function AttendancePage() {
  const [mode, setMode] = useState("checkin");
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [time, setTime] = useState(new Date());
  const [ready, setReady] = useState(true);
  const [status, setStatus] = useState("");
  const [lastAttendance, setLastAttendance] = useState(null);

  // 🔥 LIVE CLOCK
  useEffect(() => {
    const i = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  // 🔥 FORCE SCANNER RESET
  useEffect(() => {
    setReady(false);
    const t = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(t);
  }, [mode, selectedDate]);

  // Load existing attendance for the day
  const loadExistingAttendance = async (employeeId) => {
    const attendanceRef = ref(database, `attendance/${selectedDate}/${employeeId}`);
    const snapshot = await get(attendanceRef);
    return snapshot.val();
  };

  // Handle successful face recognition
  const handleAttendance = async (employee) => {
    if (!employee || !employee.id) {
      setStatus("❌ Employee data missing");
      return false;
    }

    const currentTime = time.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });

    const existing = await loadExistingAttendance(employee.id);
    const attendanceRef = ref(database, `attendance/${selectedDate}/${employee.id}`);

    try {
      if (mode === "checkin") {
        if (existing && existing.checkInTime) {
          setStatus(`⚠️ ${employee.name} already checked in at ${existing.checkInTime}`);
          return false;
        }

        const attendanceData = {
          employeeId: employee.id,
          employeeName: employee.name,
          employeeRole: employee.role || "Staff",
          date: selectedDate,
          checkInTime: currentTime,
          checkOutTime: null,
          halfDay: false,
          totalHours: 0,
          status: "checked-in",
          markedBy: "face-auth",
          timestamp: Date.now()
        };

        await set(attendanceRef, attendanceData);
        setStatus(`✅ ${employee.name} - CHECKED IN at ${currentTime}`);
        setLastAttendance({ ...attendanceData, mode: "checkin" });
        return true;

      } else if (mode === "checkout") {
        if (!existing || !existing.checkInTime) {
          setStatus(`⚠️ ${employee.name} hasn't checked in yet`);
          return false;
        }

        if (existing.checkOutTime) {
          setStatus(`⚠️ ${employee.name} already checked out at ${existing.checkOutTime}`);
          return false;
        }

        const totalHours = calculateTotalHours(existing.checkInTime, currentTime, false);
        
        const updatedData = {
          ...existing,
          checkOutTime: currentTime,
          totalHours: totalHours,
          status: "completed",
          updatedAt: Date.now()
        };

        await update(attendanceRef, updatedData);
        setStatus(`✅ ${employee.name} - CHECKED OUT at ${currentTime} | Worked: ${totalHours} hours`);
        setLastAttendance({ ...updatedData, mode: "checkout" });
        return true;

      } else if (mode === "halfday") {
        if (existing && existing.halfDay) {
          setStatus(`⚠️ ${employee.name} already marked half-day`);
          return false;
        }

        if (existing && existing.checkOutTime) {
          setStatus(`⚠️ ${employee.name} already completed full day`);
          return false;
        }

        const halfDayData = {
          employeeId: employee.id,
          employeeName: employee.name,
          employeeRole: employee.role || "Staff",
          date: selectedDate,
          checkInTime: existing?.checkInTime || currentTime,
          checkOutTime: existing?.checkOutTime || currentTime,
          halfDay: true,
          totalHours: 4.0,
          status: "half-day",
          markedBy: "face-auth",
          timestamp: Date.now()
        };

        await set(attendanceRef, halfDayData);
        setStatus(`✅ ${employee.name} - HALF DAY marked | 4 hours credited`);
        setLastAttendance({ ...halfDayData, mode: "halfday" });
        return true;
      }
    } catch (error) {
      console.error("Attendance error:", error);
      setStatus(`❌ Error: ${error.message}`);
      return false;
    }
  };

  // Check today's attendance status for employee
  const checkTodayStatus = async (employee) => {
    const existing = await loadExistingAttendance(employee.id);
    if (!existing) return "Not Started";
    if (existing.halfDay) return "Half Day";
    if (existing.checkOutTime) return "Completed";
    if (existing.checkInTime) return "Checked In";
    return "Not Started";
  };

  const formatTime = (d) =>
    d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const formatDate = (d) =>
    d.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  const currentMode = MODES.find((m) => m.key === mode);

  return (
    <div style={styles.page}>
      <Navigation role="hr" />

      {/* 🔝 HEADER */}
      <div style={styles.header}>
        <h1 style={styles.title}>🤖 AI Face Attendance</h1>

        <div style={styles.timeBox}>
          <span>📅 {formatDate(time)}</span>
          <span>⏰ {formatTime(time)}</span>
        </div>

        {/* 📅 DATE SELECT */}
        <div style={styles.dateRow}>
          <label style={styles.dateLabel}>Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            max={getTodayDate()}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={styles.dateInput}
          />
        </div>

        {/* 🔘 MODE BUTTONS */}
        <div style={styles.modeRow}>
          {MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              style={{
                ...styles.modeBtn,
                background: mode === m.key ? m.color : "#1e293b",
                border: mode === m.key ? `2px solid ${m.color}` : "2px solid #334155",
                color: mode === m.key ? "#fff" : "#94a3b8",
              }}
            >
              {m.icon} {m.label}
            </button>
          ))}
        </div>

        {/* Status Message */}
        {status && (
          <div style={styles.statusBox}>
            <p style={styles.statusText}>{status}</p>
          </div>
        )}

        {/* Last Attendance Summary */}
        {lastAttendance && (
          <div style={styles.summaryBox}>
            <h4>📋 Last Record</h4>
            <p>
              {lastAttendance.employeeName} | 
              {lastAttendance.mode === "checkin" && ` Checked In: ${lastAttendance.checkInTime}`}
              {lastAttendance.mode === "checkout" && ` Total Hours: ${lastAttendance.totalHours}h`}
              {lastAttendance.mode === "halfday" && ` Half Day - 4 hours credited`}
            </p>
          </div>
        )}
      </div>

      {/* 📷 SCANNER */}
      <div style={styles.scanner}>
        {ready && (
          <FaceScanner
            key={`${mode}-${selectedDate}`}
            mode={mode}
            selectedDate={selectedDate}
            onAttendance={handleAttendance}
            onStatusCheck={checkTodayStatus}
          />
        )}
      </div>

      {/* 📘 INSTRUCTIONS & INFO */}
    

        <div style={styles.instructionCard}>
          <h4>📋 Tips</h4>
          <ul style={styles.instructionList}>
            <li>✔ Stand 0.5–1m from camera</li>
            <li>✔ Ensure good lighting</li>
            <li>✔ Wait for voice confirmation</li>
            <li>✔ Can mark attendance for previous dates</li>
          </ul>
        </div>
      </div>

  );
}

// 🎨 STYLES
const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f172a",
    color: "#f1f5f9",
    padding: "20px",
    textAlign: "center",
  },
  header: {
    marginBottom: "20px",
  },
  title: {
    fontSize: "26px",
    fontWeight: "bold",
    marginBottom: "15px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  timeBox: {
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "400px",
    margin: "10px auto",
    fontSize: "14px",
    color: "#94a3b8",
    padding: "8px 15px",
    background: "#1e293b",
    borderRadius: "12px",
  },
  dateRow: {
    marginTop: "15px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
  },
  dateLabel: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#94a3b8",
  },
  dateInput: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #334155",
    background: "#1e293b",
    color: "#fff",
    fontSize: "14px",
    cursor: "pointer",
  },
  modeRow: {
    display: "flex",
    justifyContent: "center",
    gap: "15px",
    marginTop: "20px",
    flexWrap: "wrap",
  },
  modeBtn: {
    padding: "12px 24px",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "16px",
    transition: "all 0.3s ease",
  },
  statusBox: {
    marginTop: "15px",
    padding: "10px",
    background: "#1e293b",
    borderRadius: "8px",
    maxWidth: "500px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  statusText: {
    margin: 0,
    fontSize: "14px",
    color: "#22c55e",
    fontWeight: "bold",
  },
  summaryBox: {
    marginTop: "15px",
    padding: "10px",
    background: "#334155",
    borderRadius: "8px",
    maxWidth: "500px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  scanner: {
    marginTop: "20px",
  },
  instructions: {
    marginTop: "30px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
    maxWidth: "800px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  instructionCard: {
    background: "#1e293b",
    padding: "15px",
    borderRadius: "12px",
    textAlign: "left",
  },
  instructionList: {
    margin: "10px 0 0 20px",
    padding: 0,
    fontSize: "13px",
    color: "#94a3b8",
    lineHeight: "1.8",
  },
};